"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-16 bg-muted animate-pulse rounded-none" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative font-sans text-xs font-bold uppercase tracking-widest text-foreground/80 hover:text-primary transition-colors py-2 outline-none">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full"></span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-muted animate-pulse rounded-none" />)}
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="font-sans text-lg font-bold uppercase tracking-wider text-foreground hover:text-primary transition-colors block py-2 border-b border-border/50">
            {item.label}
          </Link>
        </div>)}
      <div className="mt-4">
        <Button className="w-full rounded-none uppercase tracking-widest text-xs font-bold py-6">
          Client Portal
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300", isScrolled ? "bg-background/90 backdrop-blur-md border-b border-border shadow-sm h-12" : "bg-background border-b border-transparent h-14")}>
      <div className="container mx-auto px-6 h-full flex items-center justify-between">
        {}
        <div className="flex items-center shrink-0">
          <Link to="/">
            <span className="font-serif text-sm tracking-wide text-primary">
              GLYPHIC
            </span>
          </Link>
        </div>

        {}
        <div className="hidden md:flex items-center gap-8">
          <MenuProvider menu_id="45">
            <DesktopNav />
          </MenuProvider>
          <div className="h-4 w-[1px] bg-border mx-2" />
          
        </div>

        {}
        <div className="flex md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-transparent">
                <Menu className="size-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px] border-l border-border bg-background p-6">
              <SheetHeader className="text-left border-b border-border pb-4 mb-4">
                <SheetTitle className="font-sans font-bold uppercase tracking-widest text-sm text-primary">
                  Navigation
                </SheetTitle>
              </SheetHeader>
              <MenuProvider menu_id="45">
                <MobileNav />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
}